<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useField } from 'vee-validate'
import { useRouter, useRoute } from 'vue-router'
import { useFormValidation } from '@/composables/useFormValidation'
import { productSchema } from '@/formValidationschemas'
import InputLabel from '@/components/general/InputLabel.vue'
import TextInput from '@/components/general/TextInput.vue'
import TextArea from '@/components/general/TextArea.vue'
import InputError from '@/components/general/InputError.vue'
import PrimaryButton from '@/components/general/PrimaryButton.vue'
import ImageUpload from '@/components/general/ImageUpload.vue'
import CustomSelect from '@/components/general/CustomSelect.vue'
import { useToast } from 'primevue/usetoast'
import api from '@/services/axios'
import { ROUTES } from '@/router/routeNames'
import ToggleSwitch from 'primevue/toggleswitch';
import mixpanel from '@/services/mixpanel';
import { useAuthStore } from '@/stores/authStore';

const router = useRouter()
const route = useRoute()
const toast = useToast()
const isSubmitting = ref(false)
const productData = ref(null)
const authStore = useAuthStore()
// const title = computed(() => (productData.value ? 'Edit Product' : 'Create Product'))

// Initialize form with useFormValidation composable
const { handleSubmit, errors, resetForm } = useFormValidation(productSchema, {
  name: productData.value?.name ?? '',
  description: productData.value?.description ?? '',
  price: productData.value?.price ?? '',
  stock: productData.value?.stock ?? '',
  category_id: productData.value?.category?.id ?? null,
  sub_category_id: productData.value?.sub_category?.id ?? null,
  is_available: productData.value?.is_available ?? true,
})

// Connect form fields
const { value: name, setValue: setName } = useField('name')
const { value: description, setValue: setDescription } = useField('description')
const { value: price, setValue: setPrice } = useField('price')
const { value: stock, setValue: setStock } = useField('stock')
const { value: category_id, setValue: setCategoryId } = useField('category_id')
const { value: sub_category_id, setValue: setSubCategoryId } = useField('sub_category_id')
const { value: is_available, setValue: setIsAvailable } = useField('is_available')

// Image handling
const image = ref(productData.value?.image ?? null)

const isCategoryValid = ref(true)
const isSubCategoryValid = ref(true)

const categories = ref([])
const subCategories = ref([])

// Fetch categories and subcategories from API
const fetchCategories = async () => {
  try {
    const response = await api.get('/api/categories')
    categories.value = response.data
  } catch (error) {
    console.error('Error fetching categories:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load categories. Please try again.',
      life: 5000,
    })
  }
}

const fetchSubCategories = async () => {
  try {
    const response = await api.get('/api/sub-categories')
    subCategories.value = response.data
  } catch (error) {
    console.error('Error fetching sub-categories:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load sub-categories. Please try again.',
      life: 5000,
    })
  }
}

// Fetch product data by UUID from route params
const fetchProduct = async (uuid) => {
  try {
    // Using the UUID to fetch the product
    const response = await api.get(`api/business/products/${uuid}`)
    productData.value = response.data

    // Update form values with fetched product data using individual setters
    setName(productData.value.name || '')
    setDescription(productData.value.description || '')
    setPrice(productData.value.price || '')
    setStock(productData.value.stock?.toString() || '')
    setCategoryId(productData.value.category?.id || null)
    setSubCategoryId(productData.value.sub_category?.id || null)
    setIsAvailable(productData.value.is_available)

    // Update image
    image.value = productData.value.image_url
  } catch (error) {
    console.error('Error fetching product:', error)

    // Check if it's an unauthorized error
    if (
      error.response &&
      (error.response.status === 403 ||
        (error.response.data && error.response.data.message === 'This action is unauthorized.'))
    ) {
      toast.add({
        severity: 'error',
        summary: 'Unauthorized',
        detail: 'You are not authorized to edit this product.',
        life: 5000,
      })

      // Redirect back to products list
      router.push({ name: ROUTES.PRODUCTS })
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load product data. Please try again.',
        life: 5000,
      })
    }
  }
}

// Fetch data when component is mounted
onMounted(() => {
  fetchCategories()
  fetchSubCategories()

  // If we have an ID in the route params, fetch the product
  const productId = route.params.id
  if (productId) {
    fetchProduct(productId)
  }
})

// Form submission handler
const submit = handleSubmit(async (values) => {
  try {
    // let message = !isCategoryValid.value ? 'category' : 'sub-category'
    // if ((!isCategoryValid.value) || (!isSubCategoryValid.value)) {
    //   toast.add({
    //     severity: 'error',
    //     summary: 'Form Incomplete',
    //     detail: `You chose 'other' for ${message}, fill the form below and click add to add a custom ${message}`,
    //     life: 5000,
    //   })
    //   isSubmitting.value = false
    //   return // Prevent form submission
    // }

    isSubmitting.value = true

    if (!isCategoryValid.value) {
      values.category_id = null
    }

    if (!isSubCategoryValid.value) {
      values.sub_category_id = null
    }

    // Create FormData for file upload
    const formData = new FormData()

    formData.append('name', values.name)
    formData.append('description', values.description)
    formData.append('price', values.price)
    formData.append('stock', values.stock)
    formData.append('is_available', values.is_available ? '1' : '0')

    if (values.category_id) {
      formData.append('category_id', values.category_id)
    }

    if (values.sub_category_id) {
      formData.append('sub_category_id', values.sub_category_id)
    }

    if (image.value && image.value instanceof File) {
      formData.append('image', image.value)
    }

    let response

    if (productData.value) {
      // Update existing product
      formData.append('_method', 'PUT') // Laravel expects _method for PUT requests
      response = await api.post(`api/business/products/${productData.value.uuid}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    } else {
      // Create new product
      response = await api.post('api/business/products', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    }
    console.log(response);
    mixpanel.track('Add/Update Product successful');


    toast.add({
      severity: 'success',
      summary: productData.value ? 'Product Updated' : 'Product Created',
      detail: productData.value
        ? 'Product has been updated successfully'
        : 'Product has been created successfully',
      life: 3000,
    })

    mixpanel.track(productData.value ? 'Product updated' : 'Product created', {
      distinct_id: authStore.user.id,
      product_name: values.name,
      product_price: values.price,
      product_stock: values.stock,
      product_category: values.category_id,
      product_sub_category: values.sub_category_id,
      product_image: values.image,
      product_is_available: values.is_available,
    });

    // Reset form and redirect
    resetForm()
    image.value = null

    // Redirect to products list
    router.push({ name: ROUTES.PRODUCTS })
  } catch (error) {
    console.error('Error saving product:', error)

    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.message || 'Failed to save product. Please try again.',
      life: 5000,
    })
  } finally {
    isSubmitting.value = false
  }
})

// Handle image upload errors
const handleImageError = (errorMessage) => {
  if (errorMessage) {
    toast.add({
      severity: 'error',
      summary: 'Image Upload Error',
      detail: errorMessage,
      life: 5000,
    });
  }
};
</script>

<template>
  <div class="sm:py-12">
    <div class="sm:px-6 lg:px-8">
      <div class="overflow-hidden bg-white shadow-sm dark:bg-gray-800 sm:rounded-lg">
        <div class="p-6 text-gray-900 dark:text-gray-100">
          <form @submit.prevent="submit">
            <div class="grid grid-cols-1 gap-6">
              <div>
                <InputLabel for="name" value="Product Name" />
                <TextInput
                  id="name"
                  v-model="name"
                  type="text"
                  class="block mt-1 w-full"
                  required
                />
                <InputError :message="errors.name" class="mt-2" />
              </div>

              <div>
                <InputLabel for="description" value="Description (Optional)" />
                <TextArea
                  id="description"
                  v-model="description"
                  class="p-4 rounded-md border border-gray-300 shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 focus:border-indigo-500 dark:focus:border-indigo-600 focus:ring-indigo-500 dark:focus:ring-indigo-600"
                />
                <InputError :message="errors.description" class="mt-2" />
              </div>

              <div>
                <InputLabel for="price" value="Price" />
                <TextInput
                  id="price"
                  v-model="price"
                  type="number"
                  step="0.01"
                  class="block mt-1 w-full"
                  required
                />
                <InputError :message="errors.price" class="mt-2" />
              </div>

              <div>
                <InputLabel for="stock" value="Stock Quantity (Optional)" />
                <TextInput id="stock" v-model="stock" type="number" class="block mt-1 w-full" />
                <InputError :message="errors.stock" class="mt-2" />
              </div>

              <div>
                <CustomSelect
                  v-model="category_id"
                  v-model:options="categories"
                  :is-valid="isCategoryValid"
                  @update:is-valid="isCategoryValid = $event"
                  label="Category"
                  :options="categories"
                  :error="errors.category_id"
                  route="/api/business/categories"
                />
                <!-- <CustomSelect
                  v-model="sub_category_id"
                  v-model:options="subCategories"
                  :is-valid="isSubCategoryValid"
                  @update:is-valid="isSubCategoryValid = $event"
                  label="Sub-Category"
                  :options="subCategories"
                  :error="errors.sub_category_id"
                  route="/api/business/sub-categories"
                /> -->
              </div>

              <div>
                <InputLabel for="image" value="Product Image" />
                <ImageUpload
                  id="image"
                  v-model="image"
                  class="mt-1"
                  :error="errors.image"
                  :preview-url="productData?.image_url"
                  :enable-compression="true"
                  :compression-options="{
                    maxWidth: 1200,
                    maxHeight: 1200,
                    quality: 0.8,
                    maxSizeMB: 1
                  }"
                  @error="handleImageError"
                />
                <InputError :message="errors.image" class="mt-2" />
              </div>

              <div>
                <InputLabel for="is_available" value="Is Available" />
                <ToggleSwitch v-model="is_available" class="mt-1" id="is_available" />
                <InputError :message="errors.is_available" class="mt-2" />
              </div>

              <div class="flex justify-end items-center">
                <PrimaryButton
                  type="submit"
                  class="ml-4"
                  :class="{ 'opacity-25': isSubmitting }"
                  :disabled="isSubmitting"
                >
                  <span v-if="isSubmitting" class="mr-2">
                    <svg
                      class="w-4 h-4 text-white animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </span>
                  {{ productData ? 'Update Product' : 'Create Product' }}
                </PrimaryButton>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
